import { Show, createEffect, createSignal } from "solid-js";
import type { GridColumns, ViewMode, ViewPreferences } from "../features/preferences/types";
import { styles } from "../styles";
import type { UserDataSource } from "../services/dataSource";

type Props = {
  open: boolean;
  dataSource: UserDataSource;
  preferences: ViewPreferences;
  onToggleOpen: () => void;
  onSaveDataSource: (value: UserDataSource) => void;
  onPreferencesChange: (preferences: ViewPreferences) => void;
};

export function SettingsPanel(props: Props) {
  const [draft, setDraft] = createSignal<UserDataSource>(props.dataSource);
  createEffect(() => setDraft(props.dataSource));
  const draftSheetId = () => {
    const value = draft();
    return value.kind === "spreadsheet" ? value.sheetId : "";
  };
  const draftEndpoint = () => {
    const value = draft();
    return value.kind === "cloudflare-d1" ? value.endpoint : "";
  };
  const draftToken = () => {
    const value = draft();
    return value.kind === "cloudflare-d1" ? value.token : "";
  };

  return (
    <div class={styles.popoverRoot}>
      <button class={styles.headerButton} type="button" title="設定" aria-label="設定" aria-expanded={props.open} aria-controls="settings-panel" onClick={props.onToggleOpen}>⚙️</button>
      <Show when={props.open}>
        <form
          id="settings-panel"
          class={styles.popover}
          style={{ width: "min(560px, calc(100vw - 2rem))", display: "grid", gap: ".75rem" }}
          onSubmit={(event) => {
            event.preventDefault();
            props.onSaveDataSource(draft());
          }}
        >
          <header class={styles.popoverHeader}>
            <strong class={styles.popoverTitle}>設定</strong>
            <button class={styles.popoverCloseButton} type="button" aria-label="設定を閉じる" onClick={props.onToggleOpen}>✕</button>
          </header>
          <div class={styles.field}>
            <span class={styles.label}>ユーザーデータの保存先</span>
            <select
              class={styles.input}
              value={draft().kind}
              onChange={(event) => setDraft(event.currentTarget.value === "cloudflare-d1"
                ? { kind: "cloudflare-d1", endpoint: "", token: "" }
                : { kind: "spreadsheet", sheetId: "" })}
            >
              <option value="spreadsheet">Googleスプレッドシート</option>
              <option value="cloudflare-d1">自前のCloudflare D1</option>
            </select>
          </div>
          <Show when={draft().kind === "spreadsheet"} fallback={(
            <div class={styles.field}>
              <span class={styles.label}>D1 Worker API</span>
              <input
                class={styles.input}
                type="url"
                value={draftEndpoint()}
                placeholder="https://your-worker.workers.dev"
                autocomplete="off"
                onInput={(event) => setDraft((current) => current.kind === "cloudflare-d1"
                  ? { ...current, endpoint: event.currentTarget.value }
                  : current)}
              />
              <span class={styles.label}>アクセストークン</span>
              <input
                class={styles.input}
                type="password"
                value={draftToken()}
                autocomplete="off"
                onInput={(event) => setDraft((current) => current.kind === "cloudflare-d1"
                  ? { ...current, token: event.currentTarget.value }
                  : current)}
              />
              <button class={styles.button} type="submit">保存・再読込</button>
            </div>
          )}>
            <div class={styles.settingSheetRow}>
              <input
                class={styles.input}
                type="text"
                value={draftSheetId()}
                placeholder="シートIDまたはスプレッドシートURL"
                autocomplete="off"
                onInput={(event) => setDraft((current) => current.kind === "spreadsheet"
                  ? { ...current, sheetId: event.currentTarget.value }
                  : current)}
              />
              <button class={styles.button} type="submit" title="スプレッドシートIDを保存して再読み込み">保存・再読込</button>
            </div>
          </Show>
          <p class={styles.settingHelp}>設定はこのブラウザのlocalStorageにのみ保存されます。D1は自分で用意したWorker APIだけに接続します。</p>
          <div class={styles.settingGrid}>
            <label class={styles.field}>
              <span class={styles.label}>一覧の表示形式</span>
              <select class={styles.input} value={props.preferences.viewMode} onChange={(event) => props.onPreferencesChange({ ...props.preferences, viewMode: event.currentTarget.value as ViewMode })}>
                <option value="lookbook">ルックブック</option>
                <option value="grid">カタログ</option>
                <option value="list">リスト</option>
              </select>
            </label>
            <label class={styles.field}>
              <div class={styles.rangeHeader}>
                <span class={styles.label}>画像表示の列数</span>
                <output>{props.preferences.gridColumns === "auto" ? "自動" : `${props.preferences.gridColumns}列`}</output>
              </div>
              <input
                class={styles.columnRange}
                type="range"
                min="0"
                max="6"
                step="1"
                value={props.preferences.gridColumns === "auto" ? 0 : props.preferences.gridColumns}
                disabled={props.preferences.viewMode === "list"}
                onInput={(event) => {
                  const value = event.currentTarget.valueAsNumber;
                  props.onPreferencesChange({ ...props.preferences, gridColumns: value === 0 ? "auto" : value as GridColumns });
                }}
                aria-label="画像表示の列数。0は自動、1から6は列数"
              />
              <div class={styles.rangeMarks} aria-hidden="true"><span>自動</span><span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span></div>
            </label>
          </div>
          <label class={styles.settingCheck}>
            <input
              type="checkbox"
              checked={props.preferences.hideUnavailablePrimagi}
              onChange={(event) => props.onPreferencesChange({
                ...props.preferences,
                hideUnavailablePrimagi: event.currentTarget.checked,
              })}
            />
            <span>PMの使用できないコーデを非表示</span>
          </label>
          <label class={styles.settingCheck}>
            <input
              type="checkbox"
              checked={props.preferences.hideCoordinateQr}
              onChange={(event) => props.onPreferencesChange({
                ...props.preferences,
                hideCoordinateQr: event.currentTarget.checked,
              })}
            />
            <span>コーデアイテムのQRを非表示</span>
          </label>
          <label class={styles.field}>
            <div class={styles.rangeHeader}>
              <span class={styles.label}>QRの自動切り替え間隔</span>
              <output>{props.preferences.qrCycleSeconds.toFixed(1)}秒</output>
            </div>
            <input
              class={styles.columnRange}
              type="range"
              min="0.3"
              max="2.5"
              step="0.1"
              value={props.preferences.qrCycleSeconds}
              onInput={(event) => props.onPreferencesChange({
                ...props.preferences,
                qrCycleSeconds: event.currentTarget.valueAsNumber,
              })}
              aria-label="QRの自動切り替え間隔"
            />
            <div class={styles.rangeEndpoints} aria-hidden="true"><span>0.3秒</span><span>2.5秒</span></div>
          </label>
          <p class={styles.settingHelp}>QR非表示を有効にしても、フレンドカードQRと所持状況の機能は影響を受けません。</p>
        </form>
      </Show>
    </div>
  );
}
